import { create } from 'zustand';
import {
  cancelTimeOff,
  createTimeOffRequest,
  listTimeOffRequests,
  transitionTimeOff,
  withdrawTimeOffRequest,
  type TimeOffCreatePayload,
} from '@/lib/timeOff';
import type { TimeOffRequest, TimeOffStatus } from '@/lib/types';

/**
 * Time-off store (Day 7).
 *
 * Caches the requests visible to the caller (RLS-scoped via
 * app_user_visible_time_off_ids). Mutations route through this store so
 * the list/detail pages stay in sync. Patterned after stores/staff.ts.
 */

interface TimeOffState {
  status: 'idle' | 'loading' | 'loaded' | 'error';
  items: TimeOffRequest[];
  error: string | null;

  load: () => Promise<void>;
  add: (payload: TimeOffCreatePayload) => Promise<TimeOffRequest>;
  withdraw: (id: string, reason?: string | null) => Promise<TimeOffRequest>;
  decide: (
    id: string,
    toStatus: Extract<TimeOffStatus, 'approved' | 'denied'>,
    note?: string | null
  ) => Promise<TimeOffRequest>;
  cancel: (id: string, reason?: string | null) => Promise<TimeOffRequest>;
  replace: (req: TimeOffRequest) => void;
  reset: () => void;
}

export const useTimeOff = create<TimeOffState>((set) => ({
  status: 'idle',
  items: [],
  error: null,

  async load() {
    set({ status: 'loading', error: null });
    try {
      const items = await listTimeOffRequests();
      set({ status: 'loaded', items });
    } catch (err) {
      set({ status: 'error', error: (err as Error).message });
    }
  },

  async add(payload) {
    const created = await createTimeOffRequest(payload);
    set((s) => ({ items: [created, ...s.items] }));
    return created;
  },

  async withdraw(id, reason = null) {
    const updated = await withdrawTimeOffRequest(id, reason);
    set((s) => ({
      items: s.items.map((it) => (it.id === id ? updated : it)),
    }));
    return updated;
  },

  async decide(id, toStatus, note = null) {
    const updated = await transitionTimeOff(id, toStatus, note);
    set((s) => ({
      items: s.items.map((it) => (it.id === id ? updated : it)),
    }));
    return updated;
  },

  async cancel(id, reason = null) {
    const updated = await cancelTimeOff(id, reason);
    set((s) => ({
      items: s.items.map((it) => (it.id === id ? updated : it)),
    }));
    return updated;
  },

  replace(req) {
    set((s) => ({
      items: s.items.map((it) => (it.id === req.id ? req : it)),
    }));
  },

  reset() {
    set({ status: 'idle', items: [], error: null });
  },
}));

export const usePendingTimeOff = () =>
  useTimeOff((s) => s.items.filter((it) => it.status === 'pending'));

export const useApprovedTimeOff = () =>
  useTimeOff((s) => s.items.filter((it) => it.status === 'approved'));
